/* eslint-disable no-empty */
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import configureStore from './store/configureStore';
import App from './App';
import registerServiceWorker from './registerServiceWorker';
import sEmitter from 'storage-emitter';
import tokenStorage, { AUTH_DATA_KEY } from './utils/tokenStorage';
import { restoreUserLogin } from './store/actions/login';
import { abortAll, getTokenManager } from './api/client';
import { registerProactiveRefresh } from './auth/proactiveRefresh';
import { clearChatbotToken } from './integrations/chatbot';
import { clearTokenCache } from './auth/session';
import debug from 'debug';
import { initDatadogLogs } from './libs/datadog-logger';

// Create debug namespaces for application initialization
const debugApp = debug('app:init');
const debugAuth = debug('app:auth');
const debugLifecycle = debug('app:lifecycle');

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

const datadogStatus = initDatadogLogs();
if (!datadogStatus.enabled && datadogStatus.reason) {
  // eslint-disable-next-line no-console
  console.info('Datadog logging disabled:', datadogStatus.reason);
}

const pathName = window.location.pathname.substring(1);
debugApp('Application starting, pathname: %s', pathName);

const IS_APP_SUBMIT_FORM =
  pathName === 'submit-application' ||
  pathName === 'guarantor-signature' ||
  pathName === 'payment-application' ||
  pathName === 'method-of-payment-authorization';

debugApp('Is app submit form: %s', IS_APP_SUBMIT_FORM);

// Get the application-wide store instance, prepopulated with state from the server where available.
const initialState = window.initialReduxState;
debugApp('Configuring Redux store with initial state: %O', initialState);
const store = configureStore(initialState);

// Subscribe to token manager logout to run app-specific handlers (Redux, Chatbot, etc.)
try {
  debugAuth('Initializing token manager logout handler');
  const tm = getTokenManager();
  if (tm) tm.onLogout = () => {
    debugAuth('Token manager logout triggered');
    try { clearTokenCache(); debugAuth('In-memory token cache cleared'); } catch {}
    try { abortAll(); debugAuth('All requests aborted'); } catch {}
    try { clearChatbotToken(); debugAuth('Chatbot token cleared'); } catch {}
    try { store.dispatch({ type: 'USER_LOGOUT' }); debugAuth('USER_LOGOUT dispatched'); } catch {}
  };
  debugAuth('Token manager logout handler registered');
} catch (e) {
  debugAuth('Failed to register token manager logout handler: %O', e);
}

// Register proactive refresh behavior using the api client tokenManager
let disposeProactive = null;
try {
  debugAuth('Registering proactive refresh (debounce: 400ms)');
  disposeProactive = registerProactiveRefresh(getTokenManager(), { debounceMs: 400 });
  debugAuth('Proactive refresh registered successfully');
} catch (e) {
  debugAuth('Failed to register proactive refresh: %O', e);
}

if (IS_APP_SUBMIT_FORM) {
  debugAuth('Submit form mode: clearing token storage');
  tokenStorage.clear();
}
if (!IS_APP_SUBMIT_FORM) {
  debugLifecycle('Setting up storage emitter listeners');
  sEmitter.on('logout', () => {
    const stack = new Error().stack;
    const caller = stack?.split('\n')[2]?.trim() || 'unknown';
    const fullStack = stack?.split('\n').slice(1, 6).map(line => line.trim()).join(' > ') || 'unknown';

    debugAuth('⚠️ STORAGE EMITTER LOGOUT EVENT RECEIVED', {
      caller,
      fullStack,
      timestamp: new Date().toISOString()
    });
    console.warn('[StorageEmitter] LOGOUT EVENT RECEIVED', {
      caller,
      fullStack,
      timestamp: new Date().toISOString()
    });
    console.log('%cTOKEN STORAGE LOGOUT', 'color: red; font-weight: bold;');

    // 🔒 Security: Clear in-memory token cache before clearing storage
    debugAuth('Securely clearing in-memory token cache');
    clearTokenCache();
    tokenStorage.clear();
    try { abortAll(); debugAuth('All requests aborted'); } catch {}
    try { clearChatbotToken(); debugAuth('Chatbot token cleared'); } catch {}
    store.dispatch({ type: 'USER_LOGOUT' });
    debugAuth('Logout complete');
  });

  sEmitter.on('tabClose', () => {
    debugLifecycle('Tab close event received, attempting restore');
    tokenStorage.tryRestore();
  });

  window.addEventListener('unload', function () {
    debugLifecycle('Window unload event triggered');
    try { disposeProactive?.(); debugAuth('Proactive refresh disposed'); } catch {}
    sEmitter.off('tabClose');
    var item = localStorage.getItem(AUTH_DATA_KEY);

    if (item) {
      debugLifecycle('Moving auth data from localStorage to sessionStorage');
      this.sessionStorage.setItem('authorizationData', item);
      localStorage.removeItem(AUTH_DATA_KEY);
      sEmitter.emit('tabClose');
      debugLifecycle('Auth data transfer complete');
    }
  });
  debugAuth('Attempting to restore token storage');
  tokenStorage.tryRestore();
}

if (!IS_APP_SUBMIT_FORM) {
  debugAuth('Dispatching restore user login action');
  store.dispatch(restoreUserLogin());
}

// Create debug instances for app-specific logging
// Note: We do NOT override console methods to preserve third-party library output
// (React DevTools, MUI warnings, axios errors, etc.)
const log = debug('app:log');
const error = debug('app:error');
const warn = debug('app:warn');

// Export debug loggers for use throughout the app
// Usage: import { appLog, appError, appWarn } from './index';
export const appLog = log;
export const appError = error;
export const appWarn = warn;

// Configure debug logging based on environment
// Production: Force disable all auth logging to prevent token exposure
if (process.env.NODE_ENV === 'production') {
  try {
    // Remove any existing debug config
    if (typeof localStorage !== 'undefined') {
      delete localStorage.debug;
    }
    // Disable all debug namespaces
    debug.enable(''); // Empty string disables all
    console.info('[Debug] Production mode: All debug logging disabled');
  } catch (e) {
    console.warn('[Debug] Cannot disable debug logging', e);
  }
}

// Development: Enable auth logging if not already set
if (process.env.NODE_ENV === 'development') {
  if (typeof localStorage !== 'undefined' && !localStorage.debug) {
    localStorage.debug = 'auth:*';
    console.info('[Debug] Development mode: Auth logging enabled (auth:*)');
  } else {
    console.info('[Debug] Development mode: Using existing debug config:', localStorage.debug);
  }
}
debugApp('Creating React root and rendering application');
const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  </React.StrictMode>,
);

debugApp('Application rendered, registering service worker');
registerServiceWorker();
debugApp('Application initialization complete');
